import React, { useState, useCallback } from 'react';
import { theme, sealColors } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { calculateProviderSovScore, shouldShowDeploymentQuestion, sovScoreToSealLevel } from '../../utils/sovScoreCalculations';
import {
  OTHER_PROVIDER_SENTINEL,
  OTHER_CATEGORIES,
  isOtherProvider,
  getOtherCategory,
  buildOtherProviderName,
} from '../../utils/otherProviderUtils';
import { OperationsScoringStep } from './OperationsScoringStep';
import type { OtherCategory } from '../../utils/otherProviderUtils';
import type {
  WorkplaceLevel, AggregatedProvider, SovScoresData, SovWeights,
  IstSelection, SovScoreResult, DeploymentModel, SubPoint, OperationsAnswers,
} from '../../types/workplace';

interface SolutionBuilderProps {
  levels: WorkplaceLevel[];
  aggregatedProviders: AggregatedProvider[];
  sovData: SovScoresData;
  sovWeights: SovWeights;
  istSelections: Record<string, IstSelection>;
  zielSelections: Record<string, IstSelection>;
  istScore: SovScoreResult;
  zielScore: SovScoreResult;
  operationsAnswers: OperationsAnswers;
  onOperationsAnswer: (qId: 'q1' | 'q2' | 'q3' | 'q4', sealValue: number) => void;
  onSelectProvider: (featureGroupId: string, provider: string | null, deployment?: DeploymentModel) => void;
  onSelectDeployment: (featureGroupId: string, deployment: DeploymentModel) => void;
  onContinue: () => void;
  onBack: () => void;
}

const DEP_KEYS: Record<DeploymentModel, string> = {
  saas: 'workplace.deployment.saas',
  'hosted-eu': 'workplace.deployment.hostedEu',
  'hosted-non-eu': 'workplace.deployment.hostedNonEu',
  'self-hosted': 'workplace.deployment.selfHosted',
};

const ALL_DEPLOYMENTS: DeploymentModel[] = ['saas', 'hosted-eu', 'hosted-non-eu', 'self-hosted'];

const CATEGORY_I18N: Record<string, string> = {
  'EU Open Source': 'workplace.other.euOpenSource',
  'Non-EU Open Source': 'workplace.other.nonEuOpenSource',
  'EU Provider (Proprietary)': 'workplace.other.euProviderProprietary',
  'Non-EU Provider (Proprietary)': 'workplace.other.nonEuProviderProprietary',
};

const selectStyle: React.CSSProperties = {
  padding: '8px 10px', border: `1px solid ${theme.border}`, borderRadius: 4, fontSize: 13, background: '#fff',
};

export const SolutionBuilder: React.FC<SolutionBuilderProps> = ({
  levels, aggregatedProviders, sovData, sovWeights,
  istSelections, zielSelections, istScore, zielScore,
  operationsAnswers, onOperationsAnswer,
  onSelectProvider, onSelectDeployment, onContinue, onBack,
}) => {
  const { t } = useLanguage();
  const [expandedLevels, setExpandedLevels] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    levels.forEach(l => { init[l.id] = true; });
    return init;
  });
  const [otherPending, setOtherPending] = useState<Record<string, boolean>>({});

  const toggleLevel = useCallback((id: string) => {
    setExpandedLevels(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const getEligible = useCallback(
    (subPoint: SubPoint) => {
      const eligibleNames = new Set(subPoint.eligibleProviders ?? []);
      return aggregatedProviders.filter(p => eligibleNames.has(p.name) && !isOtherProvider(p.name));
    },
    [aggregatedProviders],
  );

  const getSorted = useCallback(
    (subPoint: SubPoint, dep: DeploymentModel | undefined) => {
      const d = dep ?? 'saas';
      return [...getEligible(subPoint)].sort((a, b) => {
        const sa = calculateProviderSovScore(a.name, d, sovWeights, sovData).score ?? -1;
        const sb = calculateProviderSovScore(b.name, d, sovWeights, sovData).score ?? -1;
        return sb - sa;
      });
    },
    [getEligible, sovWeights, sovData],
  );

  const handleProvider = useCallback(
    (spId: string, name: string | null) => {
      if (!name) {
        onSelectProvider(spId, null);
        setOtherPending(prev => ({ ...prev, [spId]: false }));
        return;
      }
      if (name === OTHER_PROVIDER_SENTINEL) {
        setOtherPending(prev => ({ ...prev, [spId]: true }));
        onSelectProvider(spId, null);
        return;
      }
      setOtherPending(prev => ({ ...prev, [spId]: false }));
      onSelectProvider(spId, name);
      const dq = shouldShowDeploymentQuestion(name, 'hybrid', null, sovData);
      if (!dq.show && dq.autoDeployment) onSelectDeployment(spId, dq.autoDeployment);
    },
    [onSelectProvider, onSelectDeployment, sovData],
  );

  const handleOtherCategory = useCallback(
    (spId: string, category: OtherCategory) => {
      const compositeName = buildOtherProviderName(category);
      const deployment: DeploymentModel = 'saas';
      onSelectProvider(spId, compositeName, deployment);
      setOtherPending(prev => ({ ...prev, [spId]: false }));
    },
    [onSelectProvider],
  );

  const allSubPoints = levels.flatMap(l => l.subPoints);
  const assignedCount = allSubPoints.filter(sp => {
    if (sp.type === 'provider-selection') return !!zielSelections[sp.id];
    if (sp.type === 'operations-scoring') {
      return operationsAnswers.q1 !== null && operationsAnswers.q2 !== null &&
             operationsAnswers.q3 !== null && operationsAnswers.q4 !== null;
    }
    return false;
  }).length;

  return (
    <div style={{ maxWidth: 750, margin: '0 auto', padding: 16 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.text }}>
        {t('workplace.solutionBuilder.title' as any)}
      </h2>
      <p style={{ margin: '6px 0 16px', fontSize: 14, color: theme.muted }}>
        {t('workplace.solutionBuilder.description' as any)}
      </p>
      {/* Live Ist vs Ziel scores */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <ScoreBadge label={t('workplace.solutionBuilder.istScore' as any)} score={istScore.overallScore} seal={istScore.sealLevel} />
        <ScoreBadge label={t('workplace.solutionBuilder.zielScore' as any)} score={zielScore.overallScore} seal={zielScore.sealLevel} />
      </div>
      {/* Levels / Sub-Points */}
      {levels.map(level => {
        const open = expandedLevels[level.id] ?? true;
        return (
          <div key={level.id} style={{ marginBottom: 12, border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden' }}>
            <button onClick={() => toggleLevel(level.id)} style={{
              width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px', background: theme.cardBg, border: 'none', cursor: 'pointer',
              fontSize: 15, fontWeight: 700, color: theme.text,
            }}>
              <span>{level.title}</span>
              <span style={{ fontSize: 12 }}>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
              <div style={{ padding: '8px 14px 14px' }}>
                {level.subPoints.map(sp => {
                  // --- Operations Scoring sub-point ---
                  if (sp.type === 'operations-scoring') {
                    return (
                      <div key={sp.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${theme.border}` }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 8 }}>{sp.title}</div>
                        <OperationsScoringStep
                          answers={operationsAnswers}
                          onAnswer={onOperationsAnswer}
                        />
                      </div>
                    );
                  }

                  // --- Provider Selection sub-point ---
                  const ist = istSelections[sp.id] ?? null;
                  const ziel = zielSelections[sp.id] ?? null;
                  const sorted = getSorted(sp, ziel?.deployment);
                  const selProv = ziel?.provider ?? '';
                  const selDep = ziel?.deployment;
                  const isOtherSelected = isOtherProvider(selProv);
                  const isPending = otherPending[sp.id] ?? false;
                  const activeCategory = isOtherSelected ? getOtherCategory(selProv) : null;
                  const dropdownValue = isOtherSelected || isPending ? OTHER_PROVIDER_SENTINEL : selProv;

                  const depInfo = selProv && !isOtherSelected ? shouldShowDeploymentQuestion(selProv, 'hybrid', null, sovData) : null;
                  const zScore = selProv && selDep
                    ? calculateProviderSovScore(selProv, selDep, sovWeights, sovData).score : null;
                  return (
                    <div key={sp.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${theme.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{sp.title}</span>
                        {ist && <span style={{ fontSize: 11, color: theme.muted }}>
                          {t('workplace.solutionBuilder.currentIst' as any)}: {ist.provider} ({t(DEP_KEYS[ist.deployment] as any)})
                        </span>}
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select value={dropdownValue} onChange={e => handleProvider(sp.id, e.target.value || null)}
                          style={{ ...selectStyle, flex: '1 1 200px' }}>
                          <option value="">{t('workplace.solutionBuilder.unassigned' as any)}</option>
                          {sorted.map(p => {
                            const d = selDep ?? p.availableDeployments[0] ?? 'saas';
                            const ps = calculateProviderSovScore(p.name, d, sovWeights, sovData).score;
                            return <option key={p.name} value={p.name}>{p.name}{ps !== null ? ` (${ps.toFixed(0)}%)` : ''}</option>;
                          })}
                          <option disabled>{t('workplace.other.divider' as any)}</option>
                          <option value={OTHER_PROVIDER_SENTINEL}>{t('workplace.other.label' as any)}</option>
                        </select>
                        {depInfo?.show && !isOtherSelected && (
                          <select value={selDep ?? ''} onChange={e => onSelectDeployment(sp.id, e.target.value as DeploymentModel)}
                            style={{ ...selectStyle, flex: '1 1 180px' }}>
                            <option value="" disabled>{t('workplace.solutionBuilder.deploymentModel' as any)}</option>
                            {depInfo.availableDeployments.map(d => (
                              <option key={d} value={d}>{t(DEP_KEYS[d] as any)}</option>
                            ))}
                          </select>
                        )}
                        {zScore !== null && (
                          <span style={{ padding: '4px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                            color: '#fff', background: sealColors[sovScoreToSealLevel(zScore)] ?? theme.muted }}>
                            {zScore.toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {/* "Other" inline category + deployment selection */}
                      {(isPending || isOtherSelected) && (
                        <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9f9f9', borderRadius: 6, border: `1px solid ${theme.border}` }}>
                          <div style={{ fontSize: 12, color: theme.muted, marginBottom: 6 }}>
                            {t('workplace.other.selectCategory' as any)}:
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {OTHER_CATEGORIES.map(cat => (
                              <button
                                key={cat}
                                onClick={() => handleOtherCategory(sp.id, cat)}
                                style={{
                                  padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                                  cursor: 'pointer',
                                  border: activeCategory === cat ? `2px solid ${theme.secondary}` : `1px solid ${theme.border}`,
                                  background: activeCategory === cat ? theme.primarySoft : '#fff',
                                  color: theme.text,
                                }}
                              >
                                {t(CATEGORY_I18N[cat] as any)}
                              </button>
                            ))}
                          </div>
                          {isOtherSelected && (
                            <select
                              value={selDep ?? ''}
                              onChange={e => onSelectDeployment(sp.id, e.target.value as DeploymentModel)}
                              style={{ ...selectStyle, width: '100%' }}
                            >
                              <option value="" disabled>{t('workplace.solutionBuilder.deploymentModel' as any)}</option>
                              {ALL_DEPLOYMENTS.map(d => (
                                <option key={d} value={d}>{t(DEP_KEYS[d] as any)}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {/* Navigation */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button onClick={onBack} style={{
          padding: '10px 24px', border: `1px solid ${theme.border}`, background: '#fff',
          color: theme.text, fontWeight: 700, fontSize: 15, borderRadius: 4, cursor: 'pointer',
        }}>{t('workplace.solutionBuilder.back' as any)}</button>
        <button onClick={onContinue} disabled={assignedCount === 0} style={{
          padding: '10px 24px', border: `1px solid ${theme.primaryBorder}`,
          background: theme.primarySoft, color: theme.text, fontWeight: 700, fontSize: 15,
          borderRadius: 4, cursor: assignedCount > 0 ? 'pointer' : 'not-allowed',
          opacity: assignedCount > 0 ? 1 : 0.5,
        }}>{t('workplace.solutionBuilder.continue' as any)}</button>
      </div>
    </div>
  );
};

/* ---------- Helpers ---------- */
const ScoreBadge: React.FC<{ label: string; score: number | null; seal: number | null }> = ({ label, score, seal }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: 13, color: theme.muted }}>{label}</span>
    <span style={{ padding: '4px 14px', borderRadius: 12, fontWeight: 700, fontSize: 14,
      color: seal !== null ? '#fff' : theme.muted,
      background: seal !== null ? (sealColors[seal] ?? theme.muted) : theme.cardBg }}>
      {score !== null ? `${score.toFixed(1)}%` : '—'}
    </span>
  </div>
);
